/**
 * Canonical registry of the LLM models the classifier can run on. Single source
 * of truth for the app-internal model keys, the provider model-id strings, and
 * the human-readable names — imported by the classifier (which binds each key to
 * a real provider client), the database schema (`llm_model_id` column type), and
 * the reviewer UI. Adding a model here needs no DB migration; the column is text.
 */
export const LLM_MODEL_REGISTRY = {
  bedrockKimiK25: { modelId: "moonshotai.kimi-k2.5", name: "Kimi K2.5 (Bedrock)" },
  openAiGpt5Mini: { modelId: "gpt-5-mini", name: "OpenAI GPT-5 Mini" },
  openAiEuGpt5Mini: { modelId: "gpt-5-mini", name: "OpenAI EU GPT-5 Mini" },
  openAiGpt56Luna: { modelId: "gpt-5.6-luna", name: "Azure OpenAI GPT-5.6 Luna" },
  openAiEuGpt56Luna: { modelId: "gpt-5.6-luna", name: "OpenAI EU GPT-5.6 Luna" },
  openAiEuGpt6Luna: { modelId: "gpt-6-luna", name: "OpenAI EU GPT-6 Luna" },
  openAiGpt6Luna: { modelId: "gpt-6-luna", name: "Azure OpenAI GPT-6 Luna" },
  vertexGemini35Flash: { modelId: "gemini-3.5-flash", name: "Gemini 3.5 Flash (Vertex, europe-west2)" },
} as const;

/** App-internal model name (the registry key), e.g. `"openAiGpt5Mini"`. */
export type LlmModelKey = keyof typeof LLM_MODEL_REGISTRY;

/**
 * The model every classification pathway runs on by default, and the one the reviewer UI preselects.
 * Change this one line to migrate all classifier workflows to a different model.
 */
export const DEFAULT_CLASSIFICATION_LLM_MODEL_KEY: LlmModelKey = "openAiGpt56Luna";

/** Provider model-id string stored in the DB, e.g. `"gpt-5-mini"`. */
export type LlmModelId = (typeof LLM_MODEL_REGISTRY)[LlmModelKey]["modelId"];

/** Either identifier accepted at the classify boundary (resolved by `getOrDefaultLlmKey`). */
export type LlmModelIdentifier = LlmModelKey | LlmModelId;

export const llmModelKeysArray = Object.keys(LLM_MODEL_REGISTRY) as [LlmModelKey, ...LlmModelKey[]];

export interface LlmModelListing {
  id: LlmModelKey;
  name: string;
  modelId: LlmModelId;
}

export const llmModelListing: LlmModelListing[] = llmModelKeysArray.map((id) => ({
  id,
  name: LLM_MODEL_REGISTRY[id].name,
  modelId: LLM_MODEL_REGISTRY[id].modelId,
}));
